import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'

export interface DocumentListItem {
  id: string
  title: string
  updated_at: string
  created_at: string
  is_pinned: boolean
  created_by_id: string | null
  created_by_name: string | null
  created_by_avatar_url: string | null
}

export interface DocumentDetail {
  id: string
  title: string
  body: string
  updated_at: string
  created_at: string
  created_by_id: string | null
  updated_by_id: string | null
  is_pinned: boolean
}

export interface DocumentPin {
  id: string
  document_id: string
  title: string
  position: number
}

async function fetchDocuments(): Promise<DocumentListItem[]> {
  const response = await authorizedFetch('/api/documents/')
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Could not load documents'))
  }
  return (await response.json()) as DocumentListItem[]
}

async function fetchDocumentPins(): Promise<DocumentPin[]> {
  const response = await authorizedFetch('/api/documents/pins/')
  if (!response.ok) return []
  return (await response.json()) as DocumentPin[]
}

async function fetchDocument(id: string): Promise<DocumentDetail> {
  const response = await authorizedFetch(`/api/documents/${id}`)
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, 'Document not found'))
  }
  return (await response.json()) as DocumentDetail
}

export function useDocuments(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['documents'],
    queryFn: fetchDocuments,
    enabled: options?.enabled !== false,
  })
}

export function useDocumentPins(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['document-pins'],
    queryFn: fetchDocumentPins,
    enabled: options?.enabled !== false,
  })
}

export function useDocument(documentId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['document', documentId],
    queryFn: () => fetchDocument(documentId!),
    enabled: Boolean(documentId) && options?.enabled !== false,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await authorizedFetch('/api/documents/', { method: 'POST' })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to create document'))
      }
      return (await response.json()) as DocumentDetail
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['document', data.id], data)
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

export function useUpdateDocument(documentId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { title?: string; body?: string }) => {
      const response = await authorizedFetch(`/api/documents/${documentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error((err as { detail?: string }).detail || 'Failed to save document')
      }
      return (await response.json()) as DocumentDetail
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['document', documentId], data)
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document-pins'] })
    },
  })
}

export function useDuplicateDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await authorizedFetch(`/api/documents/${documentId}/duplicate/`, {
        method: 'POST',
      })
      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, 'Failed to duplicate document'))
      }
      return (await response.json()) as DocumentDetail
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await authorizedFetch(`/api/documents/${documentId}`, {
        method: 'DELETE',
      })
      if (!response.ok) {
        throw new Error('Failed to delete document')
      }
    },
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document-pins'] })
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
  })
}

export function usePinDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await authorizedFetch(`/api/documents/${documentId}/pin/`, {
        method: 'POST',
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error((err as { detail?: string }).detail || 'Failed to pin document')
      }
      return (await response.json()) as DocumentPin
    },
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document-pins'] })
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
  })
}

export function useUnpinDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (documentId: string) => {
      const response = await authorizedFetch(`/api/documents/${documentId}/pin/`, {
        method: 'DELETE',
      })
      if (!response.ok) {
        throw new Error('Failed to unpin document')
      }
    },
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['document-pins'] })
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
    },
  })
}
